import { useCallback, useMemo } from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { useDispatch } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useListRelationshipContactsQuery } from '@/features/relationshipContacts/relationshipContactsApi';
import { getReferrerEmail, useGetReferralPartnerClientCounts } from '@/hooks/useGetReferralPartnerClientCounts';
import { useDataGridColumnProps } from '@/context/columnControlContext';
import { useObjectContext } from '@/hooks/useObjectContext';
import DataGridColumnMenu from '@/components/table/DataGridComponents/DataGridColumnMenu';
import { mapColumns, parseHeadCells } from '../InstantSearchDataGrid/data';
import RelationshipCompanyActionsMenu from './RelationshipCompanyActionsMenu';
import { RELATIONSHIP_CONTACT_HEAD_CELLS } from '@/utils/relationshipContactTableSync';
import CompanyContactsToolbar from './CompanyContactsToolbar';
import ContactsNoRowsOverlay from './ContactsNoRowsOverlay';
import ContactsNoRowsErrorOverlay from './ContactsNoRowsErrorOverlay';

const RelationshipCompanyContactsTableContent = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const { relationshipCompanyId } = useObjectContext();
	const { data: contacts = [], isLoading, isError } = useListRelationshipContactsQuery(
		{ relationshipCompanyId, includeArchived: true },
		{ skip: !relationshipCompanyId },
	);

	const { referralCounts, referralCountsLoading } = useGetReferralPartnerClientCounts(contacts);

	const rows = useMemo(() => {
		const list = contacts.map((contact) => {
			const referrerEmail = getReferrerEmail(contact);
			return {
				...contact,
				fullName:
					contact.fullName ||
					[contact.firstName, contact.lastName].filter(Boolean).join(' '),
				referralCount: referrerEmail ? (referralCounts[referrerEmail] ?? 0) : null,
				referralCountLoading: Boolean(referrerEmail && referralCountsLoading),
			};
		});

		list.sort(
			(a, b) =>
				(b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0) ||
				a.fullName.localeCompare(b.fullName),
		);
		return list;
	}, [contacts, referralCounts, referralCountsLoading]);

	const columns = useMemo(
		() => [
			...parseHeadCells(RELATIONSHIP_CONTACT_HEAD_CELLS).map((column) => {
				const mapped = mapColumns({
					column: { ...column },
					objectType: 'relationshipContact',
				});
				if (mapped.field !== 'fullName') {
					return mapped;
				}
				return {
					...mapped,
					renderCell: ({ row }) => {
						const name = row.fullName || '-';
						return (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
								<Typography noWrap>{name}</Typography>
								{row.isPrimary && (
									<Chip size='small' label='Primary' color='primary' />
								)}
							</Box>
						);
					},
				};
			}),
			{
				field: 'actions',
				headerName: '',
				width: 40,
				resizable: false,
				hideable: false,
				sortable: false,
				filterable: false,
				disableColumnMenu: true,
				renderCell: ({ row }) => (
					<Box
						className='actions-cell'
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							width: '100%',
						}}
					>
						<RelationshipCompanyActionsMenu contact={row} />
					</Box>
				),
			},
		],
		[],
	);

	const {
		columns: columnsWithWidths,
		columnVisibilityModel,
		onColumnVisibilityModelChange,
		onColumnWidthChange,
	} = useDataGridColumnProps(columns);

	const handleRowClick = useCallback(
		({ row }, event) => {
			if (
				!row?.id ||
				event.target.closest(
					'button, a[href], .MuiLink-root, .has-link, .MuiCheckbox-root, input, [role="button"]',
				)
			) {
				return;
			}
			dispatch(
				openDialog({
					dialogName: 'relationshipContact',
					data: { contactId: row.id },
				}),
			);
		},
		[dispatch],
	);

	return (
		<Box
			sx={{
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: '8px',
				backgroundColor: theme.palette.background.paper,
				'& .MuiDataGrid-root': {
					borderRadius: '8px',
				},
			}}
		>
			<DataGrid
				rows={rows}
				columns={columnsWithWidths}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={onColumnVisibilityModelChange}
				onColumnWidthChange={onColumnWidthChange}
				getRowId={(row) => row.id}
				getRowHeight={() => 'auto'}
				loading={isLoading}
				disableRowSelectionOnClick
				disableColumnFilter
				hideFooter
				autoHeight
				onRowClick={handleRowClick}
				slots={{
					toolbar: CompanyContactsToolbar,
					columnMenu: DataGridColumnMenu,
					noRowsOverlay: isError ? ContactsNoRowsErrorOverlay : ContactsNoRowsOverlay,
				}}
				sx={{
					border: 'none',
					minHeight: '8em',
					'--DataGrid-overlayHeight': '3em',
					'& .MuiDataGrid-cell': {
						padding: '8px 10px',
					},
					[`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
						outline: 'none',
					},
					[`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
						{
							outline: 'none',
						},
					[`& .${gridClasses.columnHeader}`]: {
						borderRight: 'none !important',
					},
					'& .MuiDataGrid-row:hover': {
						cursor: 'pointer',
					},
				}}
			/>
		</Box>
	);
};

export default RelationshipCompanyContactsTableContent;
